import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Box, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Clock, Download } from "lucide-react";

import apiClient from "../../api-client";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import SuccessPopup from "../../components/SuccessPopup";
import { DataTable } from "../../components/DataTable";
import { Avatar } from "../../components/Avatar";
import { AgencyIcon } from "../../components/icons/AgencyIcon";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IncrementalExportModal } from "../../components/IncrementalExportModal";
import { CSV_EXPORTS_QUERY_KEY, getExportsSummary } from "../../services/csvExportService";
import {
  downloadCSV,
  getLogCSVHeaders,
  logToCSVRow,
  LOG_MODULE_LABELS,
} from "../../utils/csvUtils";

const captionTextSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: { xs: "0.7rem", md: "0.75rem" },
};

const inlineCellSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  minWidth: 0,
  width: "fit-content",
};

const formatDate = (value) =>
  new Date(value).toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

export function Logs() {
  const theme = useTheme();
  const { setPageContext, selectedFilter, filterRefresh } = useOutletContext();
  const { interceptorReady } = useAuth();
  const [activeModule, setActiveModule] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [isDownloading, setIsDownloading] = useState(false);
  const [popup, setPopup] = useState({ open: false, message: "", error: false });
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const agencyFilter = selectedFilter && selectedFilter !== "global" ? selectedFilter : null;

  useEffect(() => {
    setPage(0);
  }, [agencyFilter]);

  const queryParams = new URLSearchParams({
    page: String(page + 1),
    page_size: String(pageSize),
  });
  if (activeModule !== "all") queryParams.append("module", activeModule);
  if (search) queryParams.append("search", search);
  if (agencyFilter) queryParams.append("agency", agencyFilter);

  const { data: exportsSummary } = useQuery({
    queryKey: CSV_EXPORTS_QUERY_KEY,
    queryFn: getExportsSummary,
    enabled: interceptorReady && exportModalOpen,
    staleTime: 0,
  });
  const logsExportSummary = exportsSummary?.datasets?.find((item) => item.dataset === "logs");
  const hasActiveFilters = activeModule !== "all" || Boolean(search) || Boolean(agencyFilter);

  const handleIncrementalExported = (count) => {
    queryClient.invalidateQueries({ queryKey: CSV_EXPORTS_QUERY_KEY });
    setPopup({
      open: true,
      message: count ? `CSV descargado: ${count} logs` : "No hay logs nuevos que descargar",
      error: !count,
    });
  };

  // Vista filtrada (tab, búsqueda, agencia): no cuenta como descarga incremental
  const handleDownloadCSV = useCallback(async () => {
    setIsDownloading(true);
    try {
      const exportParams = new URLSearchParams();
      if (activeModule !== "all") exportParams.append("module", activeModule);
      if (search) exportParams.append("search", search);
      if (agencyFilter) exportParams.append("agency", agencyFilter);

      const response = await apiClient.get(`/logs/export_csv/?${exportParams.toString()}`);
      const logs = response.data.results || [];

      if (logs.length === 0) {
        setPopup({ open: true, message: "No hay logs para descargar", error: true });
        return;
      }

      const timestamp = new Date().toISOString().split("T")[0];
      const moduleSuffix = activeModule !== "all" ? `_${activeModule}` : "";
      downloadCSV(
        [getLogCSVHeaders(), ...logs.map((log) => logToCSVRow(log, LOG_MODULE_LABELS))],
        `logs${moduleSuffix}_${timestamp}`
      );
      setPopup({ open: true, message: `CSV descargado: ${logs.length} logs`, error: false });
      setExportModalOpen(false);
    } catch (error) {
      console.error("Error descargando CSV:", error);
      setPopup({ open: true, message: "Error al descargar los logs", error: true });
    } finally {
      setIsDownloading(false);
    }
  }, [activeModule, search, agencyFilter]);

  useEffect(() => {
    setPageContext({
      title: "Logs",
      subtitle: "Registro de acciones POST, PATCH y DELETE de la plataforma",
      showTitle: true,
      rightContent: (
        <Button onClick={() => setExportModalOpen(true)}>
          <Download size={16} />
          Descargar CSV
        </Button>
      ),
    });
  }, [setPageContext]);

  const { data, isLoading, isFetching } = getData(
    `/logs/?${queryParams.toString()}`,
    interceptorReady,
    filterRefresh
  );

  // Mantiene los contadores mientras carga la siguiente página/tab
  const [moduleStats, setModuleStats] = useState({});
  useEffect(() => {
    if (data?.module_stats) setModuleStats(data.module_stats);
  }, [data]);

  const tabs = useMemo(() => {
    const modules = Array.from(
      new Set([...Object.keys(LOG_MODULE_LABELS), ...Object.keys(moduleStats)])
    ).filter((module) => module !== "all");
    return [
      { value: "all", label: "Todos", count: moduleStats.all || 0 },
      ...modules.map((module) => ({
        value: module,
        label: LOG_MODULE_LABELS[module] || module,
        count: moduleStats[module] || 0,
      })),
    ];
  }, [moduleStats]);

  const columns = [
    {
      key: "created_at",
      header: "Fecha",
      minWidth: "150px",
      render: (log) => (
        <Typography variant="caption" sx={captionTextSx}>
          {formatDate(log.created_at)}
        </Typography>
      ),
    },
    {
      key: "user",
      header: "Usuario",
      minWidth: "180px",
      render: (log) => (
        <Tooltip title={log.user_email || "Anónimo"} arrow>
          <Box sx={inlineCellSx}>
            <Avatar size="sm" name={log.user_name || log.user_email || "?"} />
            <Typography variant="caption" sx={{ ...captionTextSx, fontWeight: 500 }}>
              {log.user_name || log.user_email || "Anónimo"}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "agency",
      header: "Agencia",
      minWidth: "140px",
      render: (log) => (
        <Tooltip title={log.agency_name || "-"} arrow>
          <Box sx={inlineCellSx}>
            <AgencyIcon size={20} color={theme.palette.text.secondary} />
            <Typography variant="caption" sx={captionTextSx}>
              {log.agency_name || "-"}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: "action",
      header: "Acción",
      minWidth: "220px",
      render: (log) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {log.action && (
            <Badge color="gray" dot={false}>
              {log.action}
            </Badge>
          )}
          {log.resource && (
            <Badge color="gray" dot={false}>
              {log.resource}
            </Badge>
          )}
        </Box>
      ),
    },
    {
      key: "module",
      header: "Funcionalidad",
      render: (log) => (
        <Typography variant="caption" sx={captionTextSx}>
          {LOG_MODULE_LABELS[log.module] || log.module}
        </Typography>
      ),
    },
    {
      key: "path",
      header: "Ruta",
      minWidth: "220px",
      render: (log) => (
        <Tooltip title={log.path} arrow>
          <Typography
            variant="caption"
            sx={{ ...captionTextSx, fontFamily: "monospace", display: "block", maxWidth: 360 }}
          >
            {log.path}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <DataTable
        tabs={tabs}
        activeTab={activeModule}
        onTabChange={(value) => {
          setActiveModule(value);
          setPage(0);
        }}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Buscar por acción, ruta o usuario..."
        columns={columns}
        rows={data?.results || []}
        loading={isLoading || isFetching}
        emptyIcon={Clock}
        emptyMessage="No hay logs registrados"
        tableMinWidth={1100}
        maxHeight="calc(100vh - 290px)"
        minHeight="calc(100vh - 290px)"
        page={page}
        rowsPerPage={pageSize}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(0);
        }}
      />
      <IncrementalExportModal
        open={exportModalOpen && Boolean(logsExportSummary)}
        summary={logsExportSummary}
        onClose={() => setExportModalOpen(false)}
        onExported={handleIncrementalExported}
        extraActions={
          hasActiveFilters && (
            <Button variant="ghost" onClick={handleDownloadCSV} disabled={isDownloading}>
              {isDownloading ? "Descargando..." : "Solo la vista filtrada"}
            </Button>
          )
        }
      />
      <SuccessPopup
        message={popup.message}
        isOpen={popup.open}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
        autoHideDuration={3000}
        error={popup.error}
      />
    </Box>
  );
}
